import React from 'react';
import { SelectedFile } from './StepFiles';

export interface StepConfigProps {
  mode: 'kg' | 'version';
  // Identity (KG mode)
  name?: string;
  description: string;
  dataVersion: string;
  // Files
  nodeFiles: SelectedFile[];
  edgeFiles: SelectedFile[];
  // GDS
  gdsEnabled: boolean;
  gdsLocked?: boolean;
  hasGdsLicense?: boolean;
  onGdsToggle: () => void;
  gdsError: string;
  // Version mode extras
  kgId?: string;
}

const StepConfig: React.FC<StepConfigProps> = ({
  mode, name, description, dataVersion,
  nodeFiles, edgeFiles,
  gdsEnabled, gdsLocked, hasGdsLicense, onGdsToggle, gdsError,
  kgId,
}) => {
  return (
    <div style={{ maxWidth: 560 }}>

      {/* GDS card */}
      <div className="wiz-gds-card">
        <div className="wiz-gds-row">
          <div className="wiz-gds-text">
            <div className="wiz-gds-title">Enable Graph Data Science (GDS)</div>
            <div className="wiz-gds-desc">
              Runs Neo4j GDS algorithms including PageRank, community detection,
              centrality measures, similarity scoring, and path-finding on your graph.
              Requires GDS access on your account. Contact your administrator if
              unavailable. Adds additional compute cost per pipeline run.
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={gdsEnabled}
            className={`wiz-switch${gdsEnabled ? ' wiz-switch--on' : ''}${(gdsLocked || !hasGdsLicense) ? ' wiz-switch--locked' : ''}`}
            onClick={onGdsToggle}
            disabled={gdsLocked || !hasGdsLicense}
            title={
              gdsLocked
                ? 'GDS is required for this KG — all versions must have GDS enabled'
                : !hasGdsLicense
                ? 'Your account does not have GDS access. Contact your administrator to request POLLY_KG_GDS_ORG_ADMIN.'
                : undefined
            }
            style={{ marginTop: 2 }}
          >
            <span className="wiz-switch-thumb" />
          </button>
        </div>
        {gdsLocked && (
          <div className="wiz-gds-hint">GDS is enabled for all versions of this KG and cannot be changed.</div>
        )}
        {!hasGdsLicense && !gdsLocked && (
          <div className="wiz-gds-hint">
            GDS access requires the <code>POLLY_KG_GDS_ORG_ADMIN</code> role. Contact your administrator.
          </div>
        )}
        {gdsError && (
          <div className="wiz-gds-error">{gdsError}</div>
        )}
      </div>

      {/* Review summary */}
      <div className="wiz-review-card">
        <div className="wiz-review-hd">Review</div>
        <table className="wiz-review-table">
          <tbody>
            {mode === 'kg' && (
              <tr>
                <td className="wiz-review-key">Knowledge graph</td>
                <td className="wiz-review-val wiz-review-val--mono">
                  {name || <span className="wiz-review-val--muted">—</span>}
                </td>
              </tr>
            )}
            {mode === 'version' && (
              <tr>
                <td className="wiz-review-key">KG</td>
                <td className="wiz-review-val wiz-review-val--mono">{kgId}</td>
              </tr>
            )}
            <tr>
              <td className="wiz-review-key">Description</td>
              <td className="wiz-review-val">
                {description.trim() || <span className="wiz-review-val--muted">—</span>}
              </td>
            </tr>
            <tr>
              <td className="wiz-review-key">Data version</td>
              <td className="wiz-review-val wiz-review-val--mono">
                {dataVersion.trim() || <span className="wiz-review-val--muted">None</span>}
              </td>
            </tr>
            <tr>
              <td className="wiz-review-key">Node files</td>
              <td className="wiz-review-val">
                {nodeFiles.length > 0
                  ? `${nodeFiles.length} file${nodeFiles.length !== 1 ? 's' : ''} selected`
                  : <span className="wiz-review-val--muted" style={{ color: 'var(--destructive)' }}>None (required)</span>}
              </td>
            </tr>
            <tr>
              <td className="wiz-review-key">Edge files</td>
              <td className="wiz-review-val">
                {edgeFiles.length > 0
                  ? `${edgeFiles.length} file${edgeFiles.length !== 1 ? 's' : ''} selected`
                  : <span className="wiz-review-val--muted">None</span>}
              </td>
            </tr>
            <tr>
              <td className="wiz-review-key">GDS</td>
              <td className="wiz-review-val">
                {gdsEnabled
                  ? <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Enabled</span>
                  : <span className="wiz-review-val--muted">Disabled</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StepConfig;
